/**
 * Catalog illustrations — the one Storage object that is **public on purpose**.
 *
 * Everything else the product stores is family-private: a reference drawing, an author's
 * renders, a composed scene. A medium's illustration is the opposite kind of thing — it is a
 * picture of a paper object in a shop window, shown to the marketing site and to the Studio's
 * picker before anyone signs in. So the Storage rules open `mediums/**` to the world and
 * close it to everyone but an admin for writing, and this module is the pair of functions
 * that agree on the path: the admin app writes it, the Studio and the store read it.
 *
 * It lives here rather than in either app because a path shape known in two places is a path
 * shape that drifts. `Medium.illustrationPath` is stored as a **path**, like every other
 * asset reference in the system; the URL is minted per session and never written down.
 */
import { getDownloadURL, ref } from 'firebase/storage';
/** What a catalog illustration may be. An image, and a small one. */
export const ILLUSTRATION_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
/** 5 MB. A catalog picture is a photograph of a paper object, not a print master. */
export const ILLUSTRATION_MAX_BYTES = 5 * 1024 * 1024;
/**
 * Refuse a file before the bytes move.
 *
 * Takes the two fields it actually reads rather than a `File`, so the check is unit-testable
 * under Node and this package keeps its DOM-free lib.
 */
export function checkIllustration(file) {
    if (!ILLUSTRATION_TYPES.includes(file.type))
        return 'wrong-type';
    if (file.size > ILLUSTRATION_MAX_BYTES)
        return 'too-large';
    return null;
}
function extensionFor(contentType) {
    return contentType === 'image/png' ? 'png' : contentType === 'image/webp' ? 'webp' : 'jpg';
}
/**
 * `mediums/{id}/illustration.{ext}` — one object per medium, replaced rather than versioned.
 *
 * The extension follows the content type, so replacing a PNG with a JPEG leaves the old
 * object behind; the record points at the new path and the stale one is inert. Cleaning up
 * the orphan needs a list-and-delete the browser has no rule for, so it is deliberately not
 * done here — an admin replacing an illustration should not be able to delete by accident.
 */
export function illustrationPathFor(mediumId, contentType) {
    return `mediums/${mediumId}/illustration.${extensionFor(contentType)}`;
}
/**
 * Any public `mediums/**` object as something an `<img>` or a `<video>` can load.
 *
 * Public-read by rule, so this works signed out — which is what lets the marketing build and
 * an unauthenticated Studio show the same picture.
 */
export async function mediumAssetUrl(storage, path) {
    return getDownloadURL(ref(storage, path));
}
/** A `Medium.illustrationPath` as something an `<img>` can load. */
export async function illustrationUrl(storage, illustrationPath) {
    return mediumAssetUrl(storage, illustrationPath);
}
// ─── keepsake photographs (PRD 03 §5) ────────────────────────────────────────
/**
 * A photograph of the made object — the answer to "does this exist?".
 *
 * The same public `mediums/**` tree as the illustration, and the same two rules, because it
 * is the same kind of thing: a picture of a product with nobody in it. What differs is that
 * there are several per medium and one of them may be a short silent clip, so the path
 * carries a caller-chosen key instead of a fixed name.
 */
export const PHOTO_STILL_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
/** H.264 in mp4, or webm. Both play everywhere the store runs. */
export const PHOTO_CLIP_TYPES = ['video/mp4', 'video/webm'];
/** A still is a photograph, not a print master — the illustration's own cap. */
export const PHOTO_STILL_MAX_BYTES = ILLUSTRATION_MAX_BYTES;
/**
 * 2 MB for a clip. Six to ten silent seconds of a paper object on a table compresses far
 * below this; anything above it is a video that wants to be a film, which this is not.
 */
export const PHOTO_CLIP_MAX_BYTES = 2 * 1024 * 1024;
export function checkPhoto(file, kind) {
    const types = kind === 'clip' ? PHOTO_CLIP_TYPES : PHOTO_STILL_TYPES;
    const max = kind === 'clip' ? PHOTO_CLIP_MAX_BYTES : PHOTO_STILL_MAX_BYTES;
    if (!types.includes(file.type))
        return 'wrong-type';
    if (file.size > max)
        return 'too-large';
    return null;
}
function photoExtensionFor(contentType) {
    if (contentType === 'video/mp4')
        return 'mp4';
    if (contentType === 'video/webm')
        return 'webm';
    return extensionFor(contentType);
}
/**
 * `mediums/{id}/photos/{key}.{ext}`.
 *
 * The `key` is the caller's — one per uploaded file, minted when the row is added — because
 * a medium holds several photographs and they are ordered, added and removed independently.
 * Like the illustration, a replaced file leaves its old object behind rather than deleting
 * bytes from a browser.
 */
export function photoPathFor(mediumId, key, contentType) {
    return `mediums/${mediumId}/photos/${key}.${photoExtensionFor(contentType)}`;
}
//# sourceMappingURL=clientAssets.js.map